import {
  Button,
  CheckboxField,
  ChevronDownIcon,
  ConfirmModal,
  FieldValidation,
  Form,
  Popover,
  UseFormReturn,
  XmarkIcon,
} from "@wexinc/wex-ui";
import classNames from "classnames";
import { useRef, useState } from "react";
import { knownRoles, Role, useRoleService } from "services/security";
import { OrganizationMember } from "services/organization";

interface Props {
  organizationMember: OrganizationMember;
  onClose: (selectedRoles: Role[]) => void;
  onOpen: () => void;
  canAssignOrgOwnerRole: boolean;
  canEditRoles: boolean;
  /** When-present this indicates the "Remove member"option will show.  */
  onRemoveMember?: (organizationMember: OrganizationMember) => void;
  className?: string;
}

const OrganizationMemberRoleEditor: React.FC<Props> = (props) => {
  const roleService = useRoleService();
  // filter out global roles, those are toggled via the users list
  const allRoles = roleService.roleStore.roles.filter((role) => role.allowGlobal !== true);
  const { canEditRoles, canAssignOrgOwnerRole, onRemoveMember, className, onOpen, organizationMember, onClose } = props;
  const useFormReturn = useRef<UseFormReturn>();
  const [show, setShow] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const defaultValues: { [key: string]: boolean } = allRoles.reduce((prevValue, role) => {
    prevValue[role.id] = !!organizationMember.roles.find((i) => i.id === role.id);
    return prevValue;
  }, {} as { [key: string]: boolean });

  function onFormInit(useFormReturnIncoming: UseFormReturn) {
    useFormReturn.current = useFormReturnIncoming;
  }

  function processSelections() {
    const values = useFormReturn.current?.getValues();
    const electedRoleIds = Object.entries(values as never)
      .filter((i) => i[1] === true)
      .map((i) => i[0]) as string[];
    const electedRoles = allRoles.filter((i) => electedRoleIds.indexOf(i.id) !== -1);

    if (electedRoleIds.length) {
      onClose(electedRoles);
      closePopover();
      setShowValidationError(false);
    } else {
      setShowValidationError(true);
    }
  }

  function closePopover() {
    setShowValidationError(false);
    setShow(false);
  }

  function onDelete() {
    setShow(false);
    onRemoveMember?.(organizationMember);
  }

  return (
    <>
      <Popover
        onReopen={() => setShowValidationError(false)}
        explicitOpenClose={show}
        testId="show-company-selector"
        className={classNames("w-52 p-4", className)}
        anchorButtonClassName="z-10"
        getAnchorContent={() => (
          <div
            className="flex items-center cursor-pointer"
            onClick={() => {
              show ? processSelections() : setShow(true);
              !show && onOpen?.();
            }}
            data-test-id="open-roles-menu"
          >
            <RoleBadge role={organizationMember.roles[0]} />
            {organizationMember.roles.length > 1 && (
              <RoleBadge
                isPlusIndicator
                plusIndicatorLength={organizationMember.roles.length - 1}
              />
            )}
            <ChevronDownIcon
              className="w-5 h-5 ml-2 text-neutral-dark-60 fill-current overflow-hidden"
              aria-hidden="true"
            />
          </div>
        )}
      >
        {() => (
          <>
            <Button
              testId="close-flyout"
              buttonType="secondary-neutral"
              className="absolute right-3 top-1 "
              onClick={() => processSelections()}
            >
              <XmarkIcon />
            </Button>
            {showValidationError && (
              <FieldValidation
                className="absolute top-2"
                testId="must-have-at-least-one-role"
                error="At least one role required."
              />
            )}
            <Form
              className="mt-3 h-full overflow-auto"
              onInit={onFormInit}
              options={{ defaultValues }}
            >
              <>
                {allRoles.map((i) => (
                  <CheckboxField
                    key={i.id}
                    name={i.id}
                    label={i.name}
                    containerClassName="w-full pb-4"
                    disabled={!canEditRoles || (!canAssignOrgOwnerRole && i.name === knownRoles.orgOwner)}
                  />
                ))}
                {onRemoveMember && (
                  <Button
                    className="whitespace-nowrap"
                    testId="remove-member"
                    buttonType="secondary-critical"
                    onClick={() => setShowDeleteConfirmation(true)}
                  >
                    <XmarkIcon className="fill-current mr-2" />
                    Remove member
                  </Button>
                )}
              </>
            </Form>
          </>
        )}
      </Popover>
      {showDeleteConfirmation && (
        <ConfirmModal
          onOk={onDelete}
          onCancel={() => setShowDeleteConfirmation(false)}
          title="Are you sure you wish to delete this member?"
          mode="critical"
        />
      )}
    </>
  );
};

export default OrganizationMemberRoleEditor;

interface RoleBadgeProps {
  role?: Role;
  isPlusIndicator?: boolean;
  plusIndicatorLength?: number;
}

const RoleBadge: React.FC<RoleBadgeProps> = (props) => {
  return (
    <div
      className={classNames(
        "px-2 py-0.5 bg-neutral-dark-10 rounded text-neutral-dark-70 font-semibold whitespace-nowrap",
        props.isPlusIndicator ? "ml-2" : "",
      )}
    >
      {props.role && props.role.name}
      {props.isPlusIndicator && `+${props.plusIndicatorLength}`}
    </div>
  );
};
